import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './AdminDashboard.module.scss';
import { ProductsTable } from '../../../components/ProductsTable/ProductsTable';
import { adminProduct } from '../../../types/product';
import { fetchAdminProducts, updateProductStatus, deleteProduct } from '../../../api/productApi';
import { LoadingIndicator } from '../../../components/LoadingIndicator/LoadingIndicator';
import { ConfirmDeleteModal } from '../../../components/ConfirmDeleteModal/ConfirmDeleteModal';

export const AdminDashboard = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState<'all' | 'forsale' | 'archive'>('all');
    const [products, setProducts] = useState<adminProduct[]>([]);
    const [archiveCount, setArchiveCount] = useState(0);
    const [forsaleCount, setForsaleCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState<string | null>(null);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                setLoading(true);
                const { products } = await fetchAdminProducts();
                setProducts(products);

                const archiveProducts = products.filter(p => p.status === 'archive');
                const forsaleProducts = products.filter(p => p.status === 'forsale');

                setArchiveCount(archiveProducts.length);
                setForsaleCount(forsaleProducts.length);
            } catch (error) {
                console.error('Ошибка загрузки продуктов:', error);
            } finally {
                setLoading(false);
            }
        };
        loadProducts();
    }, []);

    const handleAddProduct = () => navigate('/admin/product/add');
    const handleEditProduct = (id: string) => navigate('/admin/product/update/' + id);

    const handleOnArchivProduct = async (id: string, status: string) => {
        try {
            const newStatus = status === 'forsale' ? 'archive' : 'forsale';
            const updatedProduct = await updateProductStatus({ productId: id, status: newStatus });
            setProducts(prev =>
                prev.map(p => (p.id === id ? { ...p, status: updatedProduct.status } : p))
            );

            // Обновляем счетчики
            if (newStatus === 'archive') {
                setArchiveCount(prev => prev + 1);
                setForsaleCount(prev => prev - 1);
            } else {
                setArchiveCount(prev => prev - 1);
                setForsaleCount(prev => prev + 1);
            }
        } catch (error) {
            console.error('Ошибка изменения статуса продукта:', error);
        }
    };

    const openDeleteModal = (id: string) => {
        setProductToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!productToDelete) return;

        try {
            const product = products.find(p => p.id === productToDelete);
            await deleteProduct(productToDelete);
            setProducts(prev => prev.filter(p => p.id !== productToDelete));

            // Обновляем счетчики при удалении
            if (product?.status === 'archive') {
                setArchiveCount(prev => prev - 1);
            } else if (product?.status === 'forsale') {
                setForsaleCount(prev => prev - 1);
            }

            console.log(`Товар ${productToDelete} успешно удален`);
        } catch (error) {
            console.error('Ошибка при удалении товара:', error);
        } finally {
            setDeleteModalOpen(false);
            setProductToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setProductToDelete(null);
    };

    const filteredProducts = products.filter(product => {
        if (activeFilter === 'all') return true;
        if (activeFilter === 'forsale') return product.status === 'forsale';
        if (activeFilter === 'archive') return product.status === 'archive';
        return true;
    });

    if (loading) return <LoadingIndicator message="Загружаем список товаров..." />;

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.productsHeader}>
                <h2 className={styles.productsTitle}>Список товаров</h2>
                <button className={styles.addButton} onClick={handleAddProduct}>
                    Добавить товар
                </button>
                <div className={styles.headerLine}></div>
            </div>

            <div className={styles.filtersContainer}>
                <button
                    className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('all')}
                >
                    Все товары <span className={styles.count}>{products.length}</span>
                </button>

                <button
                    className={`${styles.filterButton} ${activeFilter === 'forsale' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('forsale')}
                >
                    В продаже <span className={styles.count}>{forsaleCount}</span>
                </button>

                <button
                    className={`${styles.filterButton} ${activeFilter === 'archive' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('archive')}
                >
                    Архивные <span className={styles.count}>{archiveCount}</span>
                </button>
            </div>

            <ProductsTable
                products={filteredProducts}
                videoStatusCounter={{archiveCount, forsaleCount}}
                onEdit={handleEditProduct}
                onDelete={openDeleteModal}
                onArchiv={handleOnArchivProduct}
                MotionWrapper={motion.div}
            />

            {deleteModalOpen && productToDelete && (() => {
                const product = products.find(p => p.id === productToDelete);
                if (!product) return null;

                return (
                    <ConfirmDeleteModal
                        onClose={handleCancelDelete}
                        onConfirm={handleConfirmDelete}
                        imageUrl={product.image}
                        imagesCount={product.images_count}
                        title={product.title}
                    />
                );
            })()}
        </div>
    );
};